/**
 * OfferCreate Page
 *
 * Create form for new offers with reward/partner dropdowns and context editor.
 * Matches real database schema (no status or date fields).
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { logger } from '@/app/utils/logger';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import { Input, Select } from '@/components/common/FormField';
import { OfferContextEditor } from '@/components/offers/OfferContextEditor';
import { PriorityValidation } from '@/components/offers/PriorityValidation';
import { DuplicateValidation } from '@/components/offers/DuplicateValidation';
import { useOfferDuplicateValidation } from '@/app/hooks/useOfferDuplicateValidation';
import { useOfferData } from '@/app/hooks/useOfferData';
import { offerService } from '@/app/services/offerService';
import { partnerService } from '@/app/services/partnerService';
import { rewardService } from '@/app/services/rewardService';
import { auditService } from '@/app/services';
import type { CreateOfferRequest, OfferContext, AssignmentStrategy } from '@/app/types/offers';
import type { PartnerResponse } from '@/app/types/partners';
import type { RewardResponse } from '@/app/types/rewards';
import { ASSIGNMENT_STRATEGY_OPTIONS } from '@/app/types/offers';
import './OfferCreate.scss';

const initialFormData: CreateOfferRequest = {
  user_id: '',
  reward_id: '',
  partner_product_id: 0,
  assignment_strategy: 'MANUAL',
  context_json: null,
  display_priority: 1,
};

export const OfferCreate: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { invalidate, refresh } = useOfferData();

  const [formData, setFormData] = useState<CreateOfferRequest>(initialFormData);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPreFilled, setIsPreFilled] = useState({ userId: false, partnerId: false });
  const [partners, setPartners] = useState<PartnerResponse[]>([]);
  const [rewards, setRewards] = useState<RewardResponse[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  // Local state for display priority input to allow empty values during editing
  const [displayPriorityInput, setDisplayPriorityInput] = useState<string>('1');

  // Sync displayPriorityInput when formData.display_priority changes externally (e.g., from PriorityValidation)
  useEffect(() => {
    if (formData.display_priority > 0) {
      setDisplayPriorityInput(formData.display_priority.toString());
    }
  }, [formData.display_priority]);

  // Check for duplicate offer
  const { isDuplicate } = useOfferDuplicateValidation(
    formData.user_id || null,
    formData.partner_product_id || null,
    formData.reward_id || null
  );

  // Fetch partners and rewards on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        const [partnersResult, rewardsResult] = await Promise.all([
          partnerService.queryPartners({ limit: 1000, is_active: true }),
          rewardService.queryRewards({ limit: 1000, status: 'ACTIVE' }),
        ]);
        setPartners(partnersResult.partners);
        setRewards(rewardsResult.rewards);
      } catch (err) {
        logger.error('Failed to load partners or rewards', err);
        setError('Failed to load required data. Please refresh the page.');
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, []);

  // Read URL parameters and pre-fill form
  useEffect(() => {
    const userId = searchParams.get('userId');
    const partnerProductId = searchParams.get('partnerProductId');

    if (userId || partnerProductId) {
      setFormData((prev) => ({
        ...prev,
        ...(userId && { user_id: userId }),
        ...(partnerProductId && { partner_product_id: parseInt(partnerProductId, 10) }),
      }));
      setIsPreFilled({
        userId: !!userId,
        partnerId: !!partnerProductId,
      });
    }
  }, [searchParams]);

  const rewardOptions = [
    { value: '', label: 'Select a reward...' },
    ...rewards
      .sort((a, b) => a.reward_name.localeCompare(b.reward_name))
      .map((r) => {
        // Build label with distinguishing information
        let label = r.reward_name;
        const parts: string[] = [];

        // Add provider if available (from redemption_config)
        if (r.redemption_config?.provider) {
          parts.push(`(${r.redemption_config.provider})`);
        }

        // Add variant if it's not the default
        if (r.reward_variant && r.reward_variant !== 'default') {
          parts.push(`[${r.reward_variant}]`);
        }

        // Add reward_key if different from name (helps distinguish duplicates)
        if (r.reward_key && r.reward_key !== r.reward_name.toLowerCase().replace(/\s+/g, '-')) {
          parts.push(`- ${r.reward_key}`);
        }

        if (parts.length > 0) {
          label = `${r.reward_name} ${parts.join(' ')}`;
        }

        return {
          value: r.reward_id,
          label: label,
        };
      }),
  ];

  const partnerOptions = [
    { value: '', label: 'Select a partner...' },
    ...partners
      .sort((a, b) =>
        (a.doing_business_as || a.partner_name).localeCompare(b.doing_business_as || b.partner_name)
      )
      .map((p) => ({
        value: p.partner_product_id.toString(),
        label: `${p.doing_business_as || p.partner_name || 'Partner'} (${p.partner_product_id})`,
      })),
  ];

  const strategyOptions = ASSIGNMENT_STRATEGY_OPTIONS.map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));

  const handleChange = useCallback(
    <K extends keyof CreateOfferRequest>(key: K, value: CreateOfferRequest[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleContextChange = useCallback((context: OfferContext | null) => {
    setFormData((prev) => ({ ...prev, context_json: context }));
  }, []);

  const validateForm = (): string | null => {
    if (!formData.user_id.trim()) {
      return 'User ID is required';
    }
    if (!formData.reward_id) {
      return 'Please select a reward';
    }
    if (!formData.partner_product_id) {
      return 'Please select a partner';
    }
    // Ensure display_priority has a valid value (default to 1 if 0 or invalid)
    if (!formData.display_priority || formData.display_priority < 1) {
      handleChange('display_priority', 1);
    }
    // Check for duplicate - prevent submission
    if (isDuplicate) {
      return 'This reward is already assigned to this user/partner combination. Please select a different reward.';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const newOffer = await offerService.createOffer(formData);

      // Log record creation for audit
      auditService
        .logRecordModification('created', 'offer', newOffer.offer_id, {
          user_id: newOffer.user_id,
          reward_id: newOffer.reward_id,
          partner_product_id: newOffer.partner_product_id,
        })
        .catch((err) => logger.warn('Failed to log record creation', { error: err }));

      invalidate();
      refresh(); // Force immediate refetch
      navigate(`/offers/${newOffer.offer_id}`);
    } catch (err) {
      setError('Failed to create offer');
      logger.error('Failed to create offer', { error: err });
    } finally {
      setSaving(false);
    }
  };

  if (loadingData) {
    return (
      <div className="offer-create-page">
        <div className="loading-state">Loading partners and rewards...</div>
      </div>
    );
  }

  return (
    <div className="offer-create-page">
      <div className="back-nav">
        <button onClick={() => navigate('/offers')} className="btn-back">
          ← Back to Offers
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {(isPreFilled.userId || isPreFilled.partnerId) && (
        <div className="pre-filled-notice">
          <span className="notice-icon">ℹ️</span>
          <span>Form pre-filled from user context</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Target User and Entities */}
        <Card>
          <CardHeader title="Offer Target" />
          <CardBody>
            <div className="form-grid">
              <div className="form-field-wrapper">
                <Input
                  label="User ID *"
                  value={formData.user_id}
                  onChange={(val) => handleChange('user_id', val)}
                  placeholder="Enter user ID"
                  disabled={isPreFilled.userId}
                />
                {isPreFilled.userId && (
                  <span className="field-locked-hint">🔒 Pre-filled from user context</span>
                )}
              </div>
              <Select
                label="Reward *"
                value={formData.reward_id}
                onChange={(val) => handleChange('reward_id', val)}
                options={rewardOptions}
              />
              <div className="form-field-wrapper">
                <Select
                  label="Partner *"
                  value={
                    formData.partner_product_id > 0 ? formData.partner_product_id.toString() : ''
                  }
                  onChange={(val) => handleChange('partner_product_id', parseInt(val, 10) || 0)}
                  options={partnerOptions}
                  disabled={isPreFilled.partnerId}
                />
                {isPreFilled.partnerId && (
                  <span className="field-locked-hint">🔒 Pre-filled from user context</span>
                )}
              </div>
            </div>
            {/* Show duplicate validation error if duplicate detected */}
            {formData.user_id && formData.partner_product_id > 0 && formData.reward_id && (
              <DuplicateValidation
                userId={formData.user_id}
                partnerProductId={formData.partner_product_id}
                rewardId={formData.reward_id}
              />
            )}
          </CardBody>
        </Card>

        {/* Offer Settings */}
        <Card>
          <CardHeader title="Offer Settings" />
          <CardBody>
            <div className="form-grid">
              <Select
                label="Assignment Strategy"
                value={formData.assignment_strategy}
                onChange={(val) => handleChange('assignment_strategy', val as AssignmentStrategy)}
                options={strategyOptions}
              />
              <Input
                label="Display Priority"
                type="number"
                value={displayPriorityInput}
                onChange={(val) => {
                  // Allow empty string and any numeric input during editing
                  setDisplayPriorityInput(val);
                  // Update form data only when we have a valid number
                  if (val !== '') {
                    const numValue = parseInt(val, 10);
                    if (!isNaN(numValue) && numValue > 0) {
                      handleChange('display_priority', numValue);
                    }
                  }
                }}
                onBlur={() => {
                  // Apply default value when field loses focus if empty
                  if (displayPriorityInput === '' || displayPriorityInput === '0') {
                    setDisplayPriorityInput('1');
                    handleChange('display_priority', 1);
                  }
                }}
              />
            </div>
            {/* Show priority validation if user and partner are set */}
            {formData.user_id && formData.partner_product_id > 0 && (
              <PriorityValidation
                userId={formData.user_id}
                partnerProductId={formData.partner_product_id}
                currentPriority={formData.display_priority}
                onPriorityChange={(priority) => handleChange('display_priority', priority)}
              />
            )}
          </CardBody>
        </Card>

        {/* Context Editor */}
        <Card>
          <CardHeader title="Context Data" />
          <CardBody>
            <p className="context-hint">
              Configure context data based on the selected assignment strategy.
            </p>
            <OfferContextEditor
              value={formData.context_json}
              strategy={formData.assignment_strategy}
              onChange={handleContextChange}
            />
          </CardBody>
        </Card>

        {/* Actions */}
        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={() => navigate('/offers')}>
            Cancel
          </button>
          <button type="submit" className="btn-create" disabled={saving || isDuplicate}>
            {saving ? 'Creating...' : 'Create Offer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OfferCreate;
